<template>
  <div
    class="payment-element-callback text-center d-flex flex-column align-items-center py-5"
  >
    <v-loader v-if="isLoading" />
    <transition name="fade-out-left" mode="out-in">
      <p :key="message" class="text-grey-60 lead">{{ message }}</p>
    </transition>
  </div>
</template>

<script>
import { mapActions } from "vuex"

import { ACTIVITY_TYPES } from "@/store/analytics"

import { isCypress } from "@/lib/is-platform"
import useStripe from "@/lib/mixins/use-stripe"
import { STAGES } from "@/lib/checkout-utils"

import { getQueryParam } from "@/lib/url-helpers"

const ATTEMPT_COUNT = isCypress() ? 2 : 15

const QUERY_PARAMS = {
  PRODUCT_ID: "product_id",
  PAYMENT_INTENT_SECRET: "payment_intent_client_secret",
  SETUP_INTENT_SECRET: "setup_intent_client_secret"
}

export default {
  mixins: [useStripe("checkStatus")],

  data() {
    return {
      isLoading: true,
      retryCount: 0,
      productId: null,
      message: this.$t(
        "components.checkout.paymentElementCallback.confirmCheckout"
      )
    }
  },

  methods: {
    ...mapActions("products", ["hasActivePurchase"]),
    ...mapActions("analytics", ["trackActivity"]),

    getParam(param) {
      return getQueryParam(window.location.href, param)
    },

    checkStatus() {
      if (this.getParam(QUERY_PARAMS.PRODUCT_ID)) {
        this.productId = this.getParam(QUERY_PARAMS.PRODUCT_ID)
      }

      if (this.getParam(QUERY_PARAMS.PAYMENT_INTENT_SECRET)) {
        this.checkStatusForPaymentIntent()
      } else if (this.getParam(QUERY_PARAMS.SETUP_INTENT_SECRET)) {
        this.checkStatusForSetupIntent()
      } else {
        this.$router.push(STAGES.PAY)
      }
    },

    checkStatusForPaymentIntent() {
      const clientSecret = this.getParam(QUERY_PARAMS.PAYMENT_INTENT_SECRET)

      if (!clientSecret) return

      this.withRetry(() =>
        this.useStripe_stripe
          .retrievePaymentIntent(clientSecret)
          .then(({ paymentIntent }) => {
            switch (paymentIntent.status) {
              case "succeeded":
              case "processing":
                this.pollForSuccess()
                break
              case "requires_payment_method":
                this.handleError({
                  reason: paymentIntent.status,
                  message: this.$t(
                    "components.checkout.paymentElementCallback.paymentNotSuccess"
                  )
                })
                break
              default:
                this.handleError({
                  reason: paymentIntent.status,
                  message: this.$t(
                    "components.checkout.paymentElementCallback.somethingError"
                  )
                })
                break
            }
          })
      )
    },

    checkStatusForSetupIntent() {
      const clientSecret = this.getParam(QUERY_PARAMS.SETUP_INTENT_SECRET)

      if (!clientSecret) return

      this.withRetry(() =>
        this.useStripe_stripe
          .retrieveSetupIntent(clientSecret)
          .then(({ setupIntent }) => {
            switch (setupIntent.status) {
              case "succeeded":
              case "processing": {
                if (this.productId) {
                  this.pollForSuccess()
                } else {
                  this.$router.removeHash()
                  this.$notify(
                    this.$t(
                      "components.checkout.paymentElementCallback.paymentSuccessUpdate"
                    )
                  )
                }
                break
              }

              case "requires_payment_method": {
                this.handleError({
                  reason: setupIntent.status,
                  message: this.$t(
                    "components.checkout.paymentElementCallback.failedProcessPaymentDetail"
                  )
                })

                break
              }
            }
          })
      )
    },

    withRetry(callback, delay = 1000, retryCount = 0) {
      try {
        callback().catch(() => {
          if (retryCount >= 10) {
            // take into account slow connections
            this.handleError({
              reason: "unknown_error",
              message: this.$t(
                "components.checkout.paymentElementCallback.somethingError"
              )
            })
          } else {
            setTimeout(() => {
              this.withRetry(callback, delay, retryCount + 1)
            }, delay)
          }
        })
      } catch (err) {
        if (retryCount >= 10) {
          this.handleError({
            reason: "unknown_error",
            message: this.$t(
              "components.checkout.paymentElementCallback.errorOccurred"
            )
          })
        } else {
          setTimeout(() => {
            this.withRetry(callback, delay, retryCount + 1)
          }, delay)
        }
      }
    },

    async pollForSuccess(attempt = 0) {
      if (attempt >= ATTEMPT_COUNT) {
        return this.handleError({
          reason: "unable_to_activate",
          message: this.$t(
            "components.checkout.paymentElementCallback.unableActivatePurchase"
          )
        })
      }

      if (attempt >= 11) {
        this.message = this.$t(
          "components.checkout.paymentElementCallback.takeLongerUsual"
        )
      } else if (attempt >= 6) {
        this.message = this.$t(
          "components.checkout.paymentElementCallback.stillWorking"
        )
      } else if (attempt >= 3) {
        this.message = this.$t(
          "components.checkout.paymentElementCallback.pleaseWaiting"
        )
      }

      if (this.productId) {
        this.hasActivePurchase(this.productId)
          .then(hasActivePurchase => {
            if (hasActivePurchase) {
              return this.handleSuccess()
            } else {
              setTimeout(() => {
                this.pollForSuccess(attempt + 1)
              }, 1000)
            }
          })
          .catch(() => {
            setTimeout(() => {
              this.pollForSuccess(attempt + 1)
            }, 1000)
          })
      } else {
        return
      }
    },

    handleSuccess() {
      this.trackActivity({
        activityType: ACTIVITY_TYPES.CHECKOUT_COMPLETED,
        data: {
          product: { id: this.productId }
        }
      })

      this.$emit("success")
    },

    handleError({ reason, message }) {
      this.trackActivity({
        activityType: ACTIVITY_TYPES.CHECKOUT_ERROR,
        data: {
          product: { id: this.productId },
          reason,
          message
        }
      })

      this.$emit("error", { reason, message })
      console.error({ reason, message })
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";

.card-element__wrapper {
  min-height: 52px;
  padding: 2px;
  padding-left: $size--2;
  background: $color-ui--white;
  border-radius: var(--input-border-radius);

  &.--focused {
    border: 2px solid rgba(var(--brand-color-rgb-vals), 0.75);
    box-shadow: 0 0 0 4px rgba(var(--brand-color-rgb-vals), 0.16);
  }
}
</style>
